// assets/AllCourses/Exercises/ListeningExercise.jsx

import React, { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StringField } from "@/components/shared/FormFields";
import { Button } from "@/components/ui/button";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function ListeningExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZED QUESTIONS ================= */
  const questions = Array.isArray(exercise.listeningQuestions)
    ? exercise.listeningQuestions.map((q) => ({
        answer: (q.wordListening || "").trim(),
        type: "text",
        skill: "listening",
      }))
    : [];

  /* ================= SAFE EXTERNAL ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer === "object" ? userAnswer : {};

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    getInputState,
  } = useExerciseEngine({
    questions,
    showResult,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC WITH PARENT (CRITICAL) ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;
  if (!Object.keys(answers).length) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= SPEECH CLEAN ================= */
  const sanitizeForSpeech = (text = "") => {
    return String(text)
      .replace(/_{2,}/g, " blank ")
      .replace(/_/g, " underscore ")
      .replace(/\s+/g, " ")
      .trim();
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("listeningQuestions", next);
  };

  const updateWord = (idx, value) => {
    if (!isEditMode) return;

    const next = [...(exercise.listeningQuestions || [])];
    next[idx].wordListening = value;

    updateQuestions(next);
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {(exercise.listeningQuestions || []).map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Word / Sentence ${idx + 1}`}
                value={q.wordListening}
                onChange={(v) => updateWord(idx, v)}
                placeholder="apple / Good morning"
                required
                error={
                  errors?.[
                    `exercise-${exercise?.index ?? idx}-listening-${idx}`
                  ]
                }
              />

              {q.wordListening && (
                <div className="flex items-center gap-3">
                  <SpeakerButton
                    text={sanitizeForSpeech(q.wordListening)}
                  />
                  <span className="text-sm text-muted-foreground">
                    Preview pronunciation
                  </span>
                </div>
              )}

              <Button
                size="sm"
                variant="destructive"
                disabled={(exercise.listeningQuestions || []).length === 1}
                onClick={() =>
                  updateQuestions(
                    exercise.listeningQuestions.filter(
                      (_, i) => i !== idx
                    )
                  )
                }
              >
                Remove
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...(exercise.listeningQuestions || []),
              { wordListening: "" },
            ])
          }
        >
          Add Word / Sentence
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No listening questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Listening Practice</CardTitle>
        <CardDescription>
          Listen and type what you hear
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, idx) => {
          const value = answers[idx] || "";

          const inputState = getInputState(
            value,
            q.answer
          );

          return (
            <div
              key={idx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <div className="flex items-center gap-4">
                <SpeakerButton
                  text={sanitizeForSpeech(q.answer)}
                  size="lg"
                />
                <span className="text-sm text-muted-foreground">
                  Click to listen
                </span>
              </div>

              <Textarea
                rows={3}
                value={value}
                placeholder="Type what you hear..."
                disabled={showResult}
                onChange={(e) => {
                  setAnswer(idx, e.target.value);
                }}
                className={cn(
                  "text-base transition border",
                  inputState.className,
                  showResult && "cursor-not-allowed opacity-95"
                )}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}